import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag, Clock, User, LogOut, Package, TrendingUp, Heart, Calendar, MapPin, CheckCircle, XCircle, Loader, MessageSquare } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { useCart } from '../contexts/CartContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ProductBrowser } from '../components/user/ProductBrowser';
import { UserProfile } from '../components/user/UserProfile';
import { CartView } from '../components/user/CartView';
import { SpecialEventModal } from '../components/modals/SpecialEventModal';
import { formatZAR } from '../utils/currency';
import { getPersonalizedGreeting } from '../utils/greeting';
type Tab = 'overview' | 'browse' | 'orders' | 'cart' | 'profile';
export function UserDashboard() {
  const {
    user,
    logout
  } = useAuth();
  const {
    orders,
    products
  } = useData();
  const {
    favoritesCount
  } = useFavorites();
  const {
    cartCount
  } = useCart();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showSpecialEventModal, setShowSpecialEventModal] = useState(false);
  // Check for active special event products
  const specialEventProducts = products.filter(product => {
    if (!product.isSpecialEvent) return false;
    const now = new Date();
    const startDate = product.specialEventStartDate ? new Date(product.specialEventStartDate) : null;
    const endDate = product.specialEventEndDate ? new Date(product.specialEventEndDate) : null;
    // Check if current date is within the event period
    if (startDate && now < startDate) return false;
    if (endDate && now > endDate) return false;
    return true;
  });
  // Show special event modal on mount and when tab changes
  useEffect(() => {
    if (specialEventProducts.length > 0) {
      setShowSpecialEventModal(true);
    }
  }, [specialEventProducts.length, activeTab]);
  // Update current time every minute for countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);
  // Filter orders for current user
  const userOrders = orders.filter(order => order.userId === user?.id);
  const recentOrders = userOrders.slice(0, 5);
  // Calculate stats
  const totalSpent = userOrders.reduce((sum, order) => sum + order.total, 0);
  const pendingOrders = userOrders.filter(order => order.status === 'Pending' || order.status === 'Order Accepted' || order.status === 'Crafting' || order.status === 'Crafting Complete' || order.status === 'Preparing Delivery' || order.status === 'Collection Ready' || order.status === 'On the Way').length;
  const completedOrders = userOrders.filter(order => order.status === 'Delivered' || order.status === 'Collected').length;
  const getTimeRemaining = (completedAt: string): string => {
    const completedTime = new Date(completedAt).getTime();
    const now = currentTime.getTime();
    const EIGHT_HOURS = 8 * 60 * 60 * 1000;
    const timeRemaining = EIGHT_HOURS - (now - completedTime);
    if (timeRemaining <= 0) return 'Removing soon...';
    const hours = Math.floor(timeRemaining / (60 * 60 * 1000));
    const minutes = Math.floor(timeRemaining % (60 * 60 * 1000) / (60 * 1000));
    return `${hours}h ${minutes}m remaining`;
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Delivered':
      case 'Collected':
        return <CheckCircle className="w-5 h-5 text-green-500" data-id="element-2950" />;
      case 'Pending':
      case 'Order Accepted':
        return <Clock className="w-5 h-5 text-yellow-500" data-id="element-2951" />;
      case 'Crafting':
      case 'Crafting Complete':
        return <Package className="w-5 h-5 text-blue-500" data-id="element-2952" />;
      case 'Preparing Delivery':
      case 'Collection Ready':
      case 'On the Way':
        return <Loader className="w-5 h-5 text-purple-500 animate-spin" data-id="element-2953" />;
      case 'Cancelled':
        return <XCircle className="w-5 h-5 text-red-500" data-id="element-2954" />;
      default:
        return <Package className="w-5 h-5 text-gray-500" data-id="element-2955" />;
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Delivered':
      case 'Collected':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'Pending':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'Order Accepted':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Crafting':
      case 'Crafting Complete':
        return 'bg-indigo-50 text-indigo-700 border-indigo-200';
      case 'Preparing Delivery':
      case 'On the Way':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'Collection Ready':
        return 'bg-teal-50 text-teal-700 border-teal-200';
      case 'Cancelled':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };
  const getOrderProgress = (status: string): number => {
    const statusOrder = ['Pending', 'Order Accepted', 'Crafting', 'Crafting Complete', 'Preparing Delivery', 'Collection Ready', 'On the Way', 'Collected', 'Delivered'];
    const index = statusOrder.indexOf(status);
    return index >= 0 ? (index + 1) / statusOrder.length * 100 : 0;
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-2956">
      <Navbar data-id="element-2957" />

      <main className="pt-24 pb-12" data-id="element-2958">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2959">
          {/* Welcome Header */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="mb-8" data-id="element-2960">
            <h1 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-2" data-id="element-2961">
              {getPersonalizedGreeting(user?.name || '', user?.surname)}!
            </h1>
            <p className="text-gray-600" data-id="element-2962">Welcome back to your dashboard</p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-2963">
            {/* Sidebar Navigation */}
            <aside className="lg:col-span-1" data-id="element-2964">
              <div className="bg-white rounded-2xl shadow-sm border border-rose-100 p-4 sticky top-24" data-id="element-2965">
                {/* User Info */}
                <div className="text-center mb-6 pb-6 border-b border-rose-50" data-id="element-2966">
                  <div className="w-20 h-20 bg-gradient-to-br from-rose-400 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-3 text-white font-bold text-2xl shadow-lg" data-id="element-2967">
                    {user?.name.charAt(0)}
                    {user?.surname?.charAt(0)}
                  </div>
                  <h2 className="font-bold text-gray-900" data-id="element-2968">
                    {user?.name} {user?.surname}
                  </h2>
                  <p className="text-sm text-gray-500 truncate" data-id="element-2969">
                    {user?.email}
                  </p>
                </div>

                {/* Navigation */}
                <nav className="space-y-1" data-id="element-2970">
                  <button onClick={() => setActiveTab('overview')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeTab === 'overview' ? 'bg-rose-50 text-rose-600 font-medium shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-2971">
                    <TrendingUp size={20} data-id="element-2972" />
                    <span data-id="element-2973">Overview</span>
                  </button>
                  <button onClick={() => setActiveTab('browse')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeTab === 'browse' ? 'bg-rose-50 text-rose-600 font-medium shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-2974">
                    <ShoppingBag size={20} data-id="element-2975" />
                    <span data-id="element-2976">Browse</span>
                  </button>
                  <button onClick={() => setActiveTab('orders')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeTab === 'orders' ? 'bg-rose-50 text-rose-600 font-medium shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-2977">
                    <Package size={20} data-id="element-2978" />
                    <span data-id="element-2979">My Orders</span>
                    {userOrders.length > 0 && <span className="ml-auto bg-rose-100 text-rose-600 text-xs font-semibold px-2 py-1 rounded-full" data-id="element-2980">
                        {userOrders.length}
                      </span>}
                  </button>
                  <button onClick={() => setActiveTab('cart')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeTab === 'cart' ? 'bg-rose-50 text-rose-600 font-medium shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-2981">
                    <ShoppingBag size={20} data-id="element-2982" />
                    <span data-id="element-2983">In Cart</span>
                    {cartCount > 0 && <span className="ml-auto bg-blue-100 text-blue-600 text-xs font-semibold px-2 py-1 rounded-full" data-id="element-2984">
                        {cartCount}
                      </span>}
                  </button>
                  <button onClick={() => setActiveTab('profile')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeTab === 'profile' ? 'bg-rose-50 text-rose-600 font-medium shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-2985">
                    <User size={20} data-id="element-2986" />
                    <span data-id="element-2987">Profile</span>
                  </button>

                  <div className="pt-4 mt-4 border-t border-rose-50" data-id="element-2988">
                    <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-500 hover:bg-red-50 transition-all" data-id="element-2989">
                      <LogOut size={20} data-id="element-2990" />
                      <span data-id="element-2991">Sign Out</span>
                    </button>
                  </div>
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <div className="lg:col-span-3" data-id="element-2992">
              {/* Overview Tab */}
              {activeTab === 'overview' && <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="space-y-6" data-id="element-2993">
                  {/* Stats Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2994">
                    <motion.div whileHover={{
                  y: -4
                }} className="bg-white rounded-xl p-6 shadow-sm border border-rose-100" data-id="element-2995">
                      <div className="flex items-center justify-between mb-2" data-id="element-2996">
                        <span className="text-sm font-medium text-gray-600" data-id="element-2997">
                          Total Orders
                        </span>
                        <Package className="w-5 h-5 text-rose-500" data-id="element-2998" />
                      </div>
                      <p className="text-3xl font-bold text-gray-900" data-id="element-2999">
                        {userOrders.length}
                      </p>
                    </motion.div>

                    <motion.div whileHover={{
                  y: -4
                }} className="bg-white rounded-xl p-6 shadow-sm border border-purple-100" data-id="element-3000">
                      <div className="flex items-center justify-between mb-2" data-id="element-3001">
                        <span className="text-sm font-medium text-gray-600" data-id="element-3002">
                          Total Spent
                        </span>
                        <TrendingUp className="w-5 h-5 text-purple-500" data-id="element-3003" />
                      </div>
                      <p className="text-3xl font-bold text-gray-900" data-id="element-3004">
                        {formatZAR(totalSpent)}
                      </p>
                    </motion.div>

                    <motion.div whileHover={{
                  y: -4
                }} className="bg-white rounded-xl p-6 shadow-sm border border-blue-100" data-id="element-3005">
                      <div className="flex items-center justify-between mb-2" data-id="element-3006">
                        <span className="text-sm font-medium text-gray-600" data-id="element-3007">
                          In Cart
                        </span>
                        <ShoppingBag className="w-5 h-5 text-blue-500" data-id="element-3008" />
                      </div>
                      <p className="text-3xl font-bold text-gray-900" data-id="element-3009">
                        {cartCount}
                      </p>
                    </motion.div>

                    <motion.div whileHover={{
                  y: -4
                }} className="bg-white rounded-xl p-6 shadow-sm border border-pink-100" data-id="element-3010">
                      <div className="flex items-center justify-between mb-2" data-id="element-3011">
                        <span className="text-sm font-medium text-gray-600" data-id="element-3012">
                          Favorites
                        </span>
                        <Heart className="w-5 h-5 text-pink-500" data-id="element-3013" />
                      </div>
                      <p className="text-3xl font-bold text-gray-900" data-id="element-3014">
                        {favoritesCount}
                      </p>
                    </motion.div>
                  </div>

                  {/* Recent Orders */}
                  <div className="bg-white rounded-2xl shadow-sm border border-rose-100 p-6" data-id="element-3015">
                    <div className="flex items-center justify-between mb-6" data-id="element-3016">
                      <h2 className="text-xl font-bold text-gray-900" data-id="element-3017">
                        Recent Orders
                      </h2>
                      <button onClick={() => setActiveTab('orders')} className="text-sm text-rose-500 hover:text-rose-600 font-medium" data-id="element-3018">
                        View All →
                      </button>
                    </div>

                    {recentOrders.length === 0 ? <div className="text-center py-12" data-id="element-3019">
                        <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" data-id="element-3020" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-3021">
                          No orders yet
                        </h3>
                        <p className="text-gray-600 mb-6" data-id="element-3022">
                          Start shopping to see your orders here!
                        </p>
                        <button onClick={() => setActiveTab('browse')} className="px-6 py-2 bg-rose-500 text-white rounded-full hover:bg-rose-600 transition-colors" data-id="element-3023">
                          Browse Products
                        </button>
                      </div> : <div className="space-y-4" data-id="element-3024">
                        {recentOrders.map((order, index) => <motion.div key={order.id} initial={{
                    opacity: 0,
                    y: 20
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: index * 0.1
                  }} className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-rose-200 hover:shadow-sm transition-all" data-id="element-3025">
                            <div className="flex-shrink-0" data-id="element-3026">
                              {getStatusIcon(order.status)}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-3027">
                              <div className="flex items-center gap-2 mb-1" data-id="element-3028">
                                <p className="font-semibold text-gray-900" data-id="element-3029">
                                  Order #{order.id}
                                </p>
                                <span className={`text-xs px-2 py-1 rounded-full border ${getStatusColor(order.status)}`} data-id="element-3030">
                                  {order.status}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 truncate" data-id="element-3031">
                                {order.items.map(item => item.name).join(', ')}
                              </p>
                              <div className="flex items-center gap-4 mt-2 text-xs text-gray-500" data-id="element-3032">
                                <span className="flex items-center gap-1" data-id="element-3033">
                                  <Calendar className="w-3 h-3" data-id="element-3034" />
                                  {new Date(order.date).toLocaleDateString()}
                                </span>
                                <span className="flex items-center gap-1" data-id="element-3035">
                                  <Package className="w-3 h-3" data-id="element-3036" />
                                  {order.items.length} item
                                  {order.items.length > 1 ? 's' : ''}
                                </span>
                              </div>
                            </div>
                            <div className="text-right" data-id="element-3037">
                              <p className="text-lg font-bold text-gray-900" data-id="element-3038">
                                {formatZAR(order.total)}
                              </p>
                            </div>
                          </motion.div>)}
                      </div>}
                  </div>
                </motion.div>}

              {/* Browse Tab */}
              {activeTab === 'browse' && <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} data-id="element-3039">
                  <div className="mb-6" data-id="element-3040">
                    <h2 className="text-2xl font-bold font-serif text-gray-900 mb-2" data-id="element-3041">
                      Shop Collection
                    </h2>
                    <p className="text-gray-600" data-id="element-3042">
                      Discover our latest gifts and arrangements
                    </p>
                  </div>
                  <ProductBrowser data-id="element-3043" />
                </motion.div>}

              {/* Orders Tab */}
              {activeTab === 'orders' && <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} data-id="element-3044">
                  <div className="mb-6" data-id="element-3045">
                    <h2 className="text-2xl font-bold font-serif text-gray-900 mb-2" data-id="element-3046">
                      Order History
                    </h2>
                    <p className="text-gray-600" data-id="element-3047">
                      Track and view all your purchases
                    </p>
                  </div>

                  {userOrders.length === 0 ? <div className="bg-white rounded-2xl shadow-sm border border-rose-100 p-12 text-center" data-id="element-3048">
                      <Package className="w-20 h-20 text-gray-300 mx-auto mb-4" data-id="element-3049" />
                      <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-3050">
                        No orders yet
                      </h3>
                      <p className="text-gray-600 mb-6" data-id="element-3051">
                        Your order history will appear here once you make a
                        purchase
                      </p>
                      <button onClick={() => setActiveTab('browse')} className="px-8 py-3 bg-rose-500 text-white rounded-full hover:bg-rose-600 transition-colors font-medium" data-id="element-3052">
                        Start Shopping
                      </button>
                    </div> : <div className="space-y-4" data-id="element-3053">
                      {userOrders.map((order, index) => <motion.div key={order.id} initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: index * 0.05
                }} className="bg-white rounded-2xl shadow-sm border border-rose-100 p-6 hover:shadow-md transition-all" data-id="element-3054">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4" data-id="element-3055">
                            <div className="flex items-center gap-3" data-id="element-3056">
                              {getStatusIcon(order.status)}
                              <div data-id="element-3057">
                                <h3 className="font-bold text-gray-900" data-id="element-3058">
                                  Order #{order.id}
                                </h3>
                                <p className="text-sm text-gray-500" data-id="element-3059">
                                  {new Date(order.date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                                </p>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-2" data-id="element-3060">
                              <span className={`text-sm px-4 py-2 rounded-full border font-medium ${getStatusColor(order.status)}`} data-id="element-3061">
                                {order.status}
                              </span>
                              {/* Countdown Timer for Completed Orders */}
                              {(order.status === 'Delivered' || order.status === 'Collected') && order.completedAt && <div className="flex items-center gap-1 text-xs text-gray-500 bg-gray-50 px-3 py-1 rounded-full" data-id="element-3062">
                                    <Clock className="w-3 h-3" data-id="element-3063" />
                                    <span data-id="element-3064">
                                      {getTimeRemaining(order.completedAt)}
                                    </span>
                                  </div>}
                            </div>
                          </div>

                          {/* Progress Bar */}
                          {order.status !== 'Cancelled' && <div className="mb-4" data-id="element-3065">
                              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-3066">
                                <div className="bg-gradient-to-r from-rose-500 to-purple-500 h-2 rounded-full transition-all duration-500" style={{
                        width: `${getOrderProgress(order.status)}%`
                      }} data-id="element-3067" />
                              </div>
                              <p className="text-xs text-gray-500 mt-1" data-id="element-3068">
                                Order Progress:{' '}
                                {Math.round(getOrderProgress(order.status))}%
                              </p>
                            </div>}

                          {/* Order Details */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 p-4 bg-gray-50 rounded-lg" data-id="element-3069">
                            <div className="flex items-center gap-2 text-sm" data-id="element-3070">
                              <Calendar className="w-4 h-4 text-gray-400" data-id="element-3071" />
                              <span className="text-gray-600" data-id="element-3072">
                                Delivery Date:
                              </span>
                              <span className="font-medium" data-id="element-3073">
                                {new Date(order.deliveryDate).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-sm" data-id="element-3074">
                              <Clock className="w-4 h-4 text-gray-400" data-id="element-3075" />
                              <span className="text-gray-600" data-id="element-3076">Time:</span>
                              <span className="font-medium" data-id="element-3077">
                                {order.preferredTime}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-sm" data-id="element-3078">
                              {order.deliveryType === 'delivery' ? <MapPin className="w-4 h-4 text-gray-400" data-id="element-3079" /> : <Package className="w-4 h-4 text-gray-400" data-id="element-3080" />}
                              <span className="text-gray-600" data-id="element-3081">Type:</span>
                              <span className="font-medium capitalize" data-id="element-3082">
                                {order.deliveryType}
                              </span>
                            </div>
                            {order.deliveryAddress && <div className="flex items-center gap-2 text-sm col-span-2" data-id="element-3083">
                                <MapPin className="w-4 h-4 text-gray-400" data-id="element-3084" />
                                <span className="text-gray-600" data-id="element-3085">Address:</span>
                                <span className="font-medium" data-id="element-3086">
                                  {order.deliveryAddress}
                                </span>
                              </div>}
                            {order.customMessage && <div className="flex items-start gap-2 text-sm col-span-2" data-id="element-3087">
                                <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5" data-id="element-3088" />
                                <span className="text-gray-600" data-id="element-3089">Message:</span>
                                <span className="font-medium italic" data-id="element-3090">
                                  "{order.customMessage}"
                                </span>
                              </div>}
                          </div>

                          <div className="space-y-3 mb-4" data-id="element-3091">
                            {order.items.map((item, idx) => <div key={idx} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0" data-id="element-3092">
                                <div data-id="element-3093">
                                  <p className="font-medium text-gray-900" data-id="element-3094">
                                    {item.name}
                                  </p>
                                  <p className="text-sm text-gray-500" data-id="element-3095">
                                    Quantity: {item.quantity}
                                  </p>
                                </div>
                                <p className="font-semibold text-gray-900" data-id="element-3096">
                                  {formatZAR(item.price * item.quantity)}
                                </p>
                              </div>)}
                          </div>

                          <div className="space-y-2 pt-4 border-t border-gray-200" data-id="element-3097">
                            <div className="flex justify-between text-sm" data-id="element-3098">
                              <span className="text-gray-600" data-id="element-3099">Subtotal</span>
                              <span className="font-medium" data-id="element-3100">
                                {formatZAR(order.total - order.deliveryFee)}
                              </span>
                            </div>
                            {order.deliveryFee > 0 && <div className="flex justify-between text-sm" data-id="element-3101">
                                <span className="text-gray-600" data-id="element-3102">
                                  Delivery Fee
                                </span>
                                <span className="font-medium" data-id="element-3103">
                                  {formatZAR(order.deliveryFee)}
                                </span>
                              </div>}
                            <div className="flex justify-between items-center pt-2 border-t border-gray-200" data-id="element-3104">
                              <span className="text-gray-600 font-medium" data-id="element-3105">
                                Total
                              </span>
                              <span className="text-2xl font-bold text-rose-500" data-id="element-3106">
                                {formatZAR(order.total)}
                              </span>
                            </div>
                          </div>
                        </motion.div>)}
                    </div>}
                </motion.div>}

              {/* Cart Tab */}
              {activeTab === 'cart' && <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} data-id="element-3107">
                  <div className="mb-6" data-id="element-3108">
                    <h2 className="text-2xl font-bold font-serif text-gray-900 mb-2" data-id="element-3109">
                      Shopping Cart
                    </h2>
                    <p className="text-gray-600" data-id="element-3110">
                      Review and manage items in your cart
                    </p>
                  </div>
                  <CartView data-id="element-3111" />
                </motion.div>}

              {/* Profile Tab */}
              {activeTab === 'profile' && <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} data-id="element-3112">
                  <div className="mb-6" data-id="element-3113">
                    <h2 className="text-2xl font-bold font-serif text-gray-900 mb-2" data-id="element-3114">
                      My Profile
                    </h2>
                    <p className="text-gray-600" data-id="element-3115">
                      Manage your account settings
                    </p>
                  </div>
                  <UserProfile data-id="element-3116" />
                </motion.div>}
            </div>
          </div>
        </div>
      </main>

      <SpecialEventModal isOpen={showSpecialEventModal} onClose={() => setShowSpecialEventModal(false)} products={specialEventProducts} data-id="element-3117" />

      <Footer data-id="element-3118" />
    </div>;
}